import type { ReactNode } from 'react';
import Animated, { FadeIn } from 'react-native-reanimated';
import { StyleSheet } from 'react-native-unistyles';
import ModalKeyboardAwareScrollView from '@/components/Custom/ModalKeyboardAwareScrollView';
import ModalKeyboardStickyView from '@/components/Custom/ModalKeyboardStickyView';
import Box from '@/components/Unistyles/Box';
import {
  WELCOME_INPUT_BOTTOM_OFFSET,
  WelcomeStepHeader,
} from './WelcomeStepFrame';

type WelcomeModalStepFrameProps = {
  title: string;
  subtitle?: string;
  children?: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
};

const CONTENT_ANIMATION_MS = 320;

export default function WelcomeModalStepFrame({
  title,
  subtitle,
  children,
  footer,
  wide = false,
}: WelcomeModalStepFrameProps) {
  return (
    <Box flex>
      <ModalKeyboardAwareScrollView
        contentContainerStyle={styles.scrollContent}
        bottomOffset={WELCOME_INPUT_BOTTOM_OFFSET}
      >
        <Animated.View
          entering={FadeIn.duration(CONTENT_ANIMATION_MS)}
          style={[styles.contentShell, wide ? styles.contentShellWide : null]}
        >
          <Box style={styles.contentArea}>
            <Box style={[styles.content, wide ? styles.contentWide : null]}>
              <WelcomeStepHeader title={title} subtitle={subtitle} />
              {children}
            </Box>
          </Box>
        </Animated.View>
      </ModalKeyboardAwareScrollView>
      {footer ? (
        <ModalKeyboardStickyView>
          <Box style={styles.footer}>{footer}</Box>
        </ModalKeyboardStickyView>
      ) : null}
    </Box>
  );
}

const styles = StyleSheet.create((theme) => ({
  content: {
    alignSelf: 'center',
    flexGrow: 1,
    gap: theme.space('xl'),
    justifyContent: 'center',
    maxWidth: theme.size(132),
    minHeight: 0,
    width: '100%',
  },
  contentArea: {
    flexGrow: 1,
    minHeight: 0,
    paddingBottom: theme.space('md'),
    paddingTop: theme.space('md'),
    width: '100%',
  },
  contentShell: {
    alignSelf: 'center',
    flexGrow: 1,
    maxWidth: theme.size(176),
    paddingHorizontal: {
      base: theme.space('lg'),
      md: theme.space('4xl'),
    },
    width: '100%',
  },
  contentShellWide: {
    maxWidth: '100%',
    paddingHorizontal: {
      base: theme.space('sm'),
      md: theme.space('md'),
    },
  },
  contentWide: {
    alignSelf: 'stretch',
    maxWidth: '100%',
  },
  footer: {
    padding: theme.space('lg'),
  },
  scrollContent: {
    flexGrow: 1,
  },
}));
